/* eslint-disable camelcase */

import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { GridSize } from "@material-ui/core";

import {
  HvDialog,
  HvGlobalActions,
  HvGrid,
  HvTag,
  HvTypography,
} from "@hitachivantara/uikit-react-core";

import DetailsKPI from "components/common/DetailsKPI";
import { DateColumnCell } from "components/common/SimpleTable";
import ProgressColumnCell, {
  normalizeProgressBar,
} from "components/common/SimpleTable/renderers/ProgressColumnCell";
import LevelBadge from "components/common/LevelBadge";
import Carousel from "components/common/Carousel";
import { OverflowTooltip } from "components/common";
import { IncidentContext } from "lib/context/IncidentContext";
import { stringFallback } from "lib/utils/fallbacks";
import { getUserDisplay } from "lib/utils/user";
import { getImageVideoUrl } from "lib/utils/url";
import useStyles from "./styles";

const IncidentDetails: React.FC = () => {
  const { t } = useTranslation("map");

  const classes = useStyles();
  const [fullScreen, setFullScreen] = useState(false);

  const { incident, type, priority, severity, status } =
    useContext(IncidentContext);

  const toggleFullScreen = () => setFullScreen(!fullScreen);

  const [currentStep, setCurrentStep] = useState(0);

  if (!incident || !type || !priority || !severity || !status) return <></>;

  const {
    pk_inc_id,
    inc_desc = "—",
    inc_source,
    update_timestamp,
    create_timestamp,
    inc_overall_completed_steps,
    inc_overall_steps,
    inc_assigned_user,
    inc_watchlist = [],
    attributes = {},
  } = incident;

  const {
    inc_type_name,
    category: { cat_name = "" } = {},
    subcategory: { sub_cat_name = "" } = {},
  } = type;

  const attributeValues = Object.values(attributes);
  const imageVideoAttributes = attributeValues
    .filter(
      (attr) =>
        (attr.contentType === "video" || attr.contentType === "image") &&
        attr.value
    )
    .map((attr) => {
      return {
        label: attr.label,
        contentType: attr.contentType as "video" | "image",
        name: (attr.type as string).split("|")[1],
        url: getImageVideoUrl(attr.value as string),
      };
    });

  const smallGridSizeProps: Record<string, GridSize> = {
    lg: imageVideoAttributes.length > 0 ? 3 : 2,
    md: imageVideoAttributes.length > 0 ? 4 : 3,
    sm: 4,
    xs: 12,
  };
  const bigGridSizeProps: Record<string, GridSize> = {
    lg: imageVideoAttributes.length > 0 ? 6 : 4,
    md: imageVideoAttributes.length > 0 ? 8 : 6,
    sm: 8,
    xs: 12,
  };

  const { prt_name, prt_color, inc_priority_order } = priority;
  const { svrt_name, svrt_color, inc_severity_order } = severity;
  const { status_name, status_color, status_text_color } = status;

  return (
    <>
      {fullScreen && (
        <HvDialog
          disableBackdropClick
          id="test"
          open={fullScreen}
          onClose={() => setFullScreen(false)}
          firstFocusable="test-close"
          classes={{
            paper: classes.paper,
            root: classes.root,
            closeButton: classes.closeButton,
          }}
          fullScreen
        >
          <div className={classes.fullscreenCarousel}>
            <Carousel
              value={imageVideoAttributes}
              fullscreen
              setCurrentStep={setCurrentStep}
              currentStep={currentStep}
            />
          </div>
        </HvDialog>
      )}
      <HvGrid container alignItems="flex-start" style={{ height: 125 }}>
        <HvGrid item xs={12}>
          <HvGlobalActions
            title={t("sections.details.title")}
            variant="section"
          />
          <HvTypography variant="normalText">
            {t("sections.details.description")}
          </HvTypography>
        </HvGrid>
      </HvGrid>
      <HvGrid container alignItems="flex-start">
        {imageVideoAttributes.length > 0 && !fullScreen && (
          <HvGrid item lg={4} md={6} xs={12}>
            <Carousel
              value={imageVideoAttributes}
              toggleFullScreen={toggleFullScreen}
              setCurrentStep={setCurrentStep}
              currentStep={currentStep}
            />
          </HvGrid>
        )}
        <HvGrid
          item
          lg={imageVideoAttributes.length > 0 ? 8 : 12}
          md={imageVideoAttributes.length > 0 ? 6 : 12}
          xs={12}
        >
          <HvGrid container>
            <HvGrid item {...smallGridSizeProps}>
              <DetailsKPI title={t("sections.details.properties.updatedDate")}>
                <DateColumnCell date={update_timestamp as string} />
              </DetailsKPI>
            </HvGrid>
            <HvGrid item {...smallGridSizeProps}>
              <DetailsKPI title={t("sections.details.properties.createdDate")}>
                <DateColumnCell date={create_timestamp as string} />
              </DetailsKPI>
            </HvGrid>
            <HvGrid item {...smallGridSizeProps}>
              <DetailsKPI title={t("sections.details.properties.status")}>
                <HvTag
                  label={
                    <HvTypography variant="normalText">
                      {status_name}
                    </HvTypography>
                  }
                  type="categorical"
                  color={status_color}
                  style={
                    status_text_color != null
                      ? { color: status_text_color }
                      : {}
                  }
                  tabIndex={-1}
                />
              </DetailsKPI>
            </HvGrid>
            <HvGrid item {...smallGridSizeProps}>
              <DetailsKPI title={t("sections.details.properties.type")}>
                {inc_type_name}
              </DetailsKPI>
            </HvGrid>
            <HvGrid item {...smallGridSizeProps}>
              <DetailsKPI title={t("sections.details.properties.category")}>
                {cat_name}
              </DetailsKPI>
            </HvGrid>
            <HvGrid item {...smallGridSizeProps}>
              <DetailsKPI title={t("sections.details.properties.subCategory")}>
                {sub_cat_name}
              </DetailsKPI>
            </HvGrid>
            <HvGrid item {...smallGridSizeProps}>
              <DetailsKPI title={t("sections.details.properties.sopProgress")}>
                {inc_overall_steps ? (
                  <>
                    <ProgressColumnCell
                      partial={inc_overall_completed_steps as number}
                      total={inc_overall_steps as number}
                      color="secondary"
                    />
                    <HvTypography style={{ paddingLeft: 10 }}>
                      {`${normalizeProgressBar(
                        inc_overall_completed_steps as number,
                        inc_overall_steps as number
                      )}%`}
                    </HvTypography>
                  </>
                ) : (
                  "—"
                )}
              </DetailsKPI>
            </HvGrid>
            <HvGrid item {...smallGridSizeProps}>
              <DetailsKPI title={t("sections.details.properties.priority")}>
                <LevelBadge
                  absolutePosition
                  name={prt_name}
                  color={prt_color as string}
                  textIcon={`P${inc_priority_order}`}
                  style={{ top: -8, left: -6 }}
                />
              </DetailsKPI>
            </HvGrid>
            <HvGrid item {...smallGridSizeProps}>
              <DetailsKPI title={t("sections.details.properties.severity")}>
                <LevelBadge
                  absolutePosition
                  name={svrt_name}
                  color={svrt_color as string}
                  textIcon={`S${inc_severity_order}`}
                  style={{ top: -8, left: -6 }}
                />
              </DetailsKPI>
            </HvGrid>
            <HvGrid item {...smallGridSizeProps}>
              <DetailsKPI title={t("sections.details.properties.id")}>
                {pk_inc_id}
              </DetailsKPI>
            </HvGrid>
            {inc_source !== undefined && (
              <HvGrid item {...smallGridSizeProps}>
                <DetailsKPI title={t("sections.details.properties.sourceId")}>
                  <OverflowTooltip data={inc_source} />
                </DetailsKPI>
              </HvGrid>
            )}
            <HvGrid item {...smallGridSizeProps}>
              <DetailsKPI title={t("sections.details.properties.assignee")}>
                <OverflowTooltip
                  data={stringFallback(getUserDisplay(inc_assigned_user))}
                />
              </DetailsKPI>
            </HvGrid>
            <HvGrid item {...smallGridSizeProps}>
              <DetailsKPI title={t("sections.details.properties.members")}>
                <HvTypography>
                  {inc_watchlist.length === 0
                    ? "—"
                    : inc_watchlist?.map((user, index) => (
                        <React.Fragment key={user.id}>
                          {getUserDisplay(user)}
                          {index <= inc_watchlist.length - 1 ? ", " : ""}
                        </React.Fragment>
                      ))}
                </HvTypography>
              </DetailsKPI>
            </HvGrid>
            <HvGrid item {...bigGridSizeProps}>
              <DetailsKPI title={t("sections.details.properties.description")}>
                {inc_desc === "" ? "—" : inc_desc}
              </DetailsKPI>
            </HvGrid>
            {attributeValues.map(
              ({ appearance, label, type: tp, contentType, value, id }) => {
                if (
                  appearance?.visible &&
                  // TODO: this needs to be consolidated
                  // images and videos not set use type, only after defined they have a content type
                  tp !== "image" &&
                  tp !== "video" &&
                  contentType !== "image" &&
                  contentType !== "video"
                ) {
                  let content: React.ReactNode = "—";
                  if (value != null) {
                    switch (tp) {
                      case "boolean":
                        content = (value as boolean)
                          ? t("sections.details.properties.trueAttribute")
                          : t("sections.details.properties.falseAttribute");
                        break;
                      case "date and time":
                        content = <DateColumnCell date={value as string} />;
                        break;
                      case "hyperlink":
                        content = (
                          <HvTypography
                            variant="normalText"
                            style={{
                              color: "#0066ff",
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                            onClick={() => {
                              window.open(value as string, "_blank");
                            }}
                          >
                            {value as string}
                          </HvTypography>
                        );
                        break;
                      default:
                        content = value as string;
                    }
                  }

                  return (
                    <HvGrid
                      item
                      {...(tp === "textarea"
                        ? bigGridSizeProps
                        : smallGridSizeProps)}
                      key={id ?? label}
                    >
                      <DetailsKPI title={label as string}>{content}</DetailsKPI>
                    </HvGrid>
                  );
                }

                return <React.Fragment key={id ?? label} />;
              }
            )}
          </HvGrid>
        </HvGrid>
      </HvGrid>
    </>
  );
};

export default IncidentDetails;
